import { useEffect, useState } from "react";

/**
 * DevTools-open detection.
 *
 * Browsers expose no API for this, so detection is a heuristic: a docked
 * DevTools panel eats a large slice of the viewport, leaving a gap between the
 * window's outer size and the page's inner size that normal browser chrome
 * never accounts for. Undocked DevTools cannot be detected this way.
 *
 * The caller uses this to pull the page image out of the DOM while the panel
 * is open, so the Elements panel has nothing to read. It is deliberately NOT
 * treated as a breach: closing DevTools restores the view.
 *
 * Known limitations - both are why this only blurs, never revokes:
 *   - a heavily zoomed-in page can trip the threshold (false positive)
 *   - DevTools in a separate window is invisible to this check (false negative)
 *
 * @param {object} opts
 * @param {boolean} opts.enabled
 * @param {number} [opts.threshold]  px gap that counts as open (default 170)
 * @param {(open: boolean) => void} [opts.onChange]
 */
export function useDevToolsGuard({ enabled, threshold = 170, onChange } = {}) {
  const [isDevToolsOpen, setIsDevToolsOpen] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setIsDevToolsOpen(false);
      return undefined;
    }

    let last = false;
    let timer = null;

    const isFsActive = () =>
      !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement
      );

    const compute = () => {
      try {
        if (typeof window === "undefined") return false;

        const outerW = window.outerWidth || 0;
        const outerH = window.outerHeight || 0;
        const innerW = window.innerWidth || 0;
        const innerH = window.innerHeight || 0;
        if (!outerW || !outerH || !innerW || !innerH) return false;

        // In full screen the outer/inner gap collapses to zero, so a docked
        // panel still shows up as a gap - the check holds.
        const widthGap = outerW - innerW;
        const heightGap = outerH - innerH;

        // Height gap alone is normal (URL bar, tabs, bookmarks), so it needs a
        // bigger allowance than the width gap unless we are in full screen.
        const heightAllowance = isFsActive() ? threshold : threshold + 130;

        return widthGap > threshold || heightGap > heightAllowance;
      } catch (e) {
        return false;
      }
    };

    const check = () => {
      const now = compute();
      if (now !== last) {
        last = now;
        setIsDevToolsOpen(now);
        if (typeof onChange === "function") onChange(now);
      }
    };

    check();
    window.addEventListener("resize", check, false);
    // Docking / undocking does not always fire resize, so poll gently too.
    timer = setInterval(check, 1000);

    return () => {
      window.removeEventListener("resize", check, false);
      if (timer) clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, threshold]);

  return { isDevToolsOpen };
}

export default useDevToolsGuard;
